<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import AddressIcon from '@/assets/icons/counterparty/master/address.svg';
import ContactsIcon from '@/assets/icons/counterparty/master/contacts.svg';
import BankDetailsIcon from '@/assets/icons/counterparty/master/bank-details.svg';
import SubordinationIcon from '@/assets/icons/counterparty/master/subordination.svg';
import CommentIcon from '@/assets/icons/counterparty/master/comment.svg';
import type { IconComponent } from '@/modules/common/types/icon.type.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { useUserStore } from '@/modules/core/stores/user.store.ts';
import { useCounterpartyMasterAccess } from '@/modules/counterparty/counterparty-master/composables/use-counterparty-master-access.ts';
import { createSchema } from '@/modules/ui/models/schema/schema.ts';
import { SchemaCombobox } from '@/modules/ui/models/schema/schema-combobox.ts';
import { SchemaHost } from '@/modules/ui/models/schema/schema-host.ts';
import { SchemaTileList } from '@/modules/ui/models/schema/schema-tile-list.ts';
import { useWizardStep } from '@/providers/wizard.ts';
import { useCounterpartyMaster } from '@/modules/counterparty/counterparty-master/counterparty-master.ts';
import type { CounterpartyManagerSections } from '@/modules/counterparty/counterparty-master/types/manager-form.ts';
import { CounterpartyOptionsUtil } from '@/modules/counterparty/utils/counterparty-options.util.ts';
import { applyManagerForm } from '@/modules/counterparty/counterparty-master/utils/apply-manager-form.ts';
import { filterSectionsByAccess } from '@/modules/counterparty/counterparty-master/utils/counterparty-master-access.util.ts';
import { useManagerDirectoryFields } from '@/modules/counterparty/counterparty-master/composables/use-manager-directory-fields.ts';
import { resolveDirectoryFieldState } from '@/modules/counterparty/counterparty-master/consts/directory.const.ts';
import {
  isPendingDirectoryId,
  mergeDirectoryOptions,
} from '@/modules/counterparty/counterparty-master/utils/pending-directory.util.ts';
import UiForm from '@/modules/ui/components/ui/UiForm.vue';
import DirectoryNameFormModal from '@/modules/counterparty/counterparty-master/components/DirectoryNameFormModal.vue';

type FormData = {
  manager: Option<number> | null;
  segment: Option<number> | null;
  region: Option<number> | null;
  city: Option<string> | null;
  sections: CounterpartyManagerSections;
};

const SECTION_TILES = [
  {
    code: 'addresses',
    label: 'counterparty.master.steps.manager.tile.addresses',
    icon: AddressIcon,
  },
  {
    code: 'contacts',
    label: 'counterparty.master.steps.manager.tile.contacts',
    icon: ContactsIcon,
  },
  {
    code: 'bankDetails',
    label: 'counterparty.master.steps.manager.tile.bankDetails',
    icon: BankDetailsIcon,
  },
  {
    code: 'subordination',
    label: 'counterparty.master.steps.manager.tile.subordination',
    icon: SubordinationIcon,
  },
  {
    code: 'comment',
    label: 'counterparty.master.steps.manager.tile.comment',
    icon: CommentIcon,
  },
] as const satisfies readonly {
  code: keyof CounterpartyManagerSections;
  label: string;
  icon: IconComponent;
}[];

const { formData } = useCounterpartyMaster();
const { isValid, showErrors } = useWizardStep();
const formRef = useTemplateRef('form');
const userStore = useUserStore();
const masterAccess = useCounterpartyMasterAccess();
const { modal, startAdd, startEdit, submit, remove, isNameTaken } = useManagerDirectoryFields(formData, formRef);

const segmentRequired = ref(false);
const regionRequired = ref(false);
const segmentPlaceholder = ref('counterparty.field.segment');
const regionPlaceholder = ref('counterparty.field.region');

const canAddSegment = computed(() => masterAccess.value.segmentsAdd);
const canEditSegment = computed(() => masterAccess.value.segmentsEdit);
const canAddRegion = computed(() => masterAccess.value.regionsAdd);
const canEditRegion = computed(() => masterAccess.value.regionsEdit);
const canDeleteDirectory = computed(() => {
  const canEdit = modal.field === 'segment' ? canEditSegment.value : canEditRegion.value;

  return canEdit && modal.itemId != null && isPendingDirectoryId(modal.itemId);
});

const showRequiredFieldsError = computed(() => showErrors.value && !isValid.value);

function isDirectoryNameTaken(name: string) {
  return isNameTaken(modal.field, name, modal.mode === 'edit' ? modal.itemId : null);
}

const sectionTiles = computed(() => SECTION_TILES.filter((tile) => masterAccess.value[tile.code]));

function syncDirectoryField(field: 'segment' | 'region', options: Option<number>[]) {
  const state = resolveDirectoryFieldState(field, options, formData.value[field]);

  if (field === 'segment') {
    segmentRequired.value = state.required;
    segmentPlaceholder.value = state.placeholderKey;
  } else {
    regionRequired.value = state.required;
    regionPlaceholder.value = state.placeholderKey;
  }

  if (formData.value[field]?.code !== state.value?.code) {
    formData.value = { ...formData.value, [field]: state.value };
  }
}

function syncManagerFromDirectory(options: Option<number>[]) {
  const current = formData.value.manager;

  if (current && !options.some((option) => option.code === current.code)) {
    formData.value = { ...formData.value, manager: null };
  }

  if (!userStore.isDefined || formData.value.manager) {
    return;
  }

  const { id, name } = userStore.required;

  if (!options.some((option) => option.code === id)) {
    return;
  }

  formData.value = {
    ...formData.value,
    manager: { code: id, label: name },
  };
}

async function getManagerOptions(signal: AbortSignal): Promise<Option<number>[]> {
  const options = (await CounterpartyOptionsUtil.managersById(signal)).filter(
    (option): option is Option<number> => typeof option.code === 'number'
  );

  syncManagerFromDirectory(options);
  return options;
}

async function getSegmentOptions(signal: AbortSignal): Promise<Option<number>[]> {
  const options = mergeDirectoryOptions(
    'segment',
    (await CounterpartyOptionsUtil.segmentsById(signal)).filter(
      (option): option is Option<number> => typeof option.code === 'number'
    )
  );
  syncDirectoryField('segment', options);
  return options;
}

async function getRegionOptions(signal: AbortSignal): Promise<Option<number>[]> {
  const options = mergeDirectoryOptions(
    'region',
    (await CounterpartyOptionsUtil.regionsById(signal)).filter(
      (option): option is Option<number> => typeof option.code === 'number'
    )
  );
  syncDirectoryField('region', options);
  return options;
}

const schema = computed(() =>
  createSchema(
    new SchemaCombobox({
      name: 'manager',
      placeholder: 'counterparty.master.steps.manager.field.manager',
      options: getManagerOptions,
      updateAfterLoad: true,
      required: true,
      withSearch: true,
      inlineSearch: true,
      expanded: true,
      indent: 'spacer',
    }),
    new SchemaCombobox({
      name: 'segment',
      placeholder: segmentPlaceholder.value,
      options: getSegmentOptions,
      required: segmentRequired.value,
      noClear: false,
      withSearch: true,
      inlineSearch: true,
      expanded: true,
      updateAfterLoad: true,
      searchMaxLength: 255,
      pinSelected: true,
      highlightMatches: true,
      isOptionEditable: () => canEditSegment.value,
      editOption: canEditSegment.value ? (option) => startEdit('segment', option) : undefined,
      addOption: canAddSegment.value ? () => startAdd('segment') : undefined,
      addOptionLabel: canAddSegment.value ? 'counterparty.master.steps.manager.directory.addNew' : undefined,
      indent: 'spacer',
    }),
    new SchemaCombobox({
      name: 'region',
      placeholder: regionPlaceholder.value,
      options: getRegionOptions,
      required: regionRequired.value,
      noClear: false,
      withSearch: true,
      inlineSearch: true,
      expanded: true,
      updateAfterLoad: true,
      searchMaxLength: 255,
      pinSelected: true,
      highlightMatches: true,
      isOptionEditable: () => canEditRegion.value,
      editOption: canEditRegion.value ? (option) => startEdit('region', option) : undefined,
      addOption: canAddRegion.value ? () => startAdd('region') : undefined,
      addOptionLabel: canAddRegion.value ? 'counterparty.master.steps.manager.directory.addNew' : undefined,
      indent: 'spacer',
    }),
    masterAccess.value.city &&
      new SchemaCombobox({
        name: 'city',
        placeholder: 'counterparty.master.steps.manager.field.city',
        options: CounterpartyOptionsUtil.cities,
        required: true,
        withCustomOption: true,
        inlineSearch: true,
        updateAfterLoad: true,
        expanded: true,
        searchMaxLength: 255,
        indent: 'spacer',
      }),
    showRequiredFieldsError.value &&
      new SchemaHost({
        name: 'requiredFieldsError',
        slot: 'requiredFieldsError',
      }),
    sectionTiles.value.length &&
      new SchemaTileList({
        name: 'sections',
        type: 'checkbox',
        options: sectionTiles.value,
        indent: 'spread',
      })
  )
);

const formModel = computed({
  get: (): FormData => ({
    manager: formData.value.manager,
    segment: formData.value.segment,
    region: formData.value.region,
    city: formData.value.city,
    sections: formData.value.sections,
  }),
  set: ({ manager, segment, region, city, sections }: FormData) => {
    const allowedSections = filterSectionsByAccess(sections, masterAccess.value);

    formData.value = applyManagerForm(formData.value, {
      manager,
      segment,
      region,
      city,
      sections: allowedSections,
    });
  },
});
</script>

<template>
  <div class="step-manager">
    <UiForm
      ref="form"
      v-model="formModel"
      v-model:valid="isValid"
      v-model:show-errors="showErrors"
      type="compact"
      hide-field-error-messages
      :schema
    >
      <template #requiredFieldsError>
        <p class="step-manager__error">
          {{ $t('counterparty.master.steps.manager.error.fillRequiredFields') }}
        </p>
      </template>
    </UiForm>

    <DirectoryNameFormModal
      v-model="modal.open"
      :field="modal.field"
      :mode="modal.mode"
      :initial-name="modal.name"
      :initial-note="modal.note"
      :initial-center="modal.center"
      :initial-tz-id="modal.tzId"
      :loading="modal.loading"
      :can-delete="canDeleteDirectory"
      :is-name-taken="isDirectoryNameTaken"
      @submit="submit"
      @delete="remove"
    />
  </div>
</template>

<style scoped lang="scss">
.step-manager {
  display: flex;
  height: 100%;
  min-width: 0;
  flex-direction: column;

  :deep(.ui-form) {
    display: flex;
    flex: 1 1 auto;
    height: 100%;
    min-width: 0;
    min-height: 0;
    flex-direction: column;
  }

  :deep(.ui-form__fields) {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    flex-direction: column;
  }

  :deep(.ui-field) {
    min-width: 0;
    max-width: 100%;
  }

  :deep(.ui-field._indent-spread) {
    margin-top: auto;
  }

  :deep(.ui-tile-list) {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: stretch;
    gap: 8px;
    margin: 0;
  }

  :deep(.ui-tile) {
    display: flex;
    flex: 0 0 calc((100% - 16px) / 3);
    width: calc((100% - 16px) / 3);
    max-width: calc((100% - 16px) / 3);
    min-width: 0;
    min-height: calc(16px + 2 * var(--line-height-l) + 8px + 24px);
    margin: 0;
    box-sizing: border-box;
    padding-top: 8px;
    padding-bottom: 8px;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .label {
      margin-bottom: 8px;
      text-align: center;
      color: var(--color-gray-700);
    }

    .icon {
      flex-shrink: 0;
      width: 24px;
      height: 24px;
    }
  }
}

.step-manager__error {
  margin: 0;
  color: var(--color-red-600);
  font-size: 12px;
  line-height: var(--line-height);
}
</style>
